import { ToggleButtonGroup, ToggleButton } from '@heroui/react';
import type { Key } from 'react';

/**
 * Pick one option out of a few — the app's single answer for that question.
 *
 * There were three of these. `AppearanceTab` had a private pill row,
 * `VoicePreferencesTab` had a private `ModeButton` row, and the same file also
 * rendered HeroUI's `ToggleButtonGroup` whenever the Hero skin was on — so a
 * setting was written twice, in two shapes, and which one you saw depended on
 * the skin. Four settings in one modal, each drawn a different way, is what
 * makes an interface feel assembled rather than designed.
 *
 * Built on HeroUI's ToggleButtonGroup rather than another hand-rolled row: it
 * is the house component for this, and React Aria brings the part the custom
 * versions never had — arrow-key movement between options, roving focus, and
 * correct radio-group semantics for screen readers. The old pill row was a
 * line of separate buttons that a keyboard user had to tab through one by one.
 */
export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
}

export function Segmented<T extends string | number>(props: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Accessible name for the group — say what is being chosen. */
  label?: string;
  /** Stretch to the container. Off for compact rows in a settings list. */
  fullWidth?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { value, onChange, options } = props;
  return (
    <ToggleButtonGroup
      aria-label={props.label}
      selectionMode="single"
      // Without this, clicking the selected option clears it and the setting
      // has no value at all — there is no "none" for any of these.
      disallowEmptySelection
      selectedKeys={[String(value)]}
      onSelectionChange={(keys: Set<Key>) => {
        const next = [...keys][0];
        if (next === undefined) return;
        // Keys come back as strings; hand the caller its own type back.
        const picked = options.find((o) => String(o.value) === String(next));
        if (picked) onChange(picked.value);
      }}
      size={props.size ?? 'sm'}
      fullWidth={props.fullWidth}
      className={props.className}
    >
      {options.map((o, i) => (
        <ToggleButton key={String(o.value)} id={String(o.value)}>
          {i > 0 && <ToggleButtonGroup.Separator />}
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
